import React from "react";
import type { ReplyAcknowledgementResult } from "../api";

export type ReplyAcknowledgementNotice =
  | "newer_reply_available"
  | "acknowledgement_failed"
  | "reload_failed";

export interface ReplyAcknowledgementEffectProps {
  requestId: string | null | undefined;
  token: string | null | undefined;
  acknowledge: (
    requestId: string,
    observedLatestInboundMessageId: string,
  ) => Promise<ReplyAcknowledgementResult>;
  reload: () => Promise<boolean>;
  onNotice?: (notice: ReplyAcknowledgementNotice, error?: unknown) => void;
}

export function ReplyAcknowledgementEffect({
  requestId,
  token,
  acknowledge,
  reload,
  onNotice,
}: ReplyAcknowledgementEffectProps) {
  const acknowledgeRef = React.useRef(acknowledge);
  const reloadRef = React.useRef(reload);
  const onNoticeRef = React.useRef(onNotice);

  React.useEffect(() => {
    acknowledgeRef.current = acknowledge;
    reloadRef.current = reload;
    onNoticeRef.current = onNotice;
  }, [acknowledge, reload, onNotice]);

  React.useEffect(() => {
    if (!requestId || !token) return;
    let active = true;

    void (async () => {
      let result: ReplyAcknowledgementResult;
      try {
        result = await acknowledgeRef.current(requestId, token);
      } catch (error) {
        if (active) onNoticeRef.current?.("acknowledgement_failed", error);
        return;
      }
      if (!active) return;

      let reloaded: boolean;
      try {
        reloaded = await reloadRef.current();
      } catch (error) {
        if (active) onNoticeRef.current?.("reload_failed", error);
        return;
      }
      if (!active) return;
      if (!reloaded) {
        onNoticeRef.current?.("reload_failed");
        return;
      }
      if (result.status === "newer_reply_available") {
        onNoticeRef.current?.("newer_reply_available");
      }
    })();

    return () => {
      active = false;
    };
  }, [requestId, token]);

  return null;
}
